import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusBadge, money } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreditCard, ReceiptText } from "lucide-react";

export default function Payments() {
  const { data: payments = [], isLoading } = useQuery({ queryKey: ["payments"], queryFn: async () => (await api.get("/payments")).data });
  const { data: receipts = [] } = useQuery({ queryKey: ["receipts"], queryFn: async () => (await api.get("/receipts")).data });

  return (
    <div>
      <PageHeader title="Payments & Receipts" subtitle="Verified transactions across all gateways" testid="payments-header" />
      {isLoading ? <Loading /> : (
        <Tabs defaultValue="payments">
          <TabsList><TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger><TabsTrigger value="receipts" data-testid="tab-receipts">Receipts</TabsTrigger></TabsList>
          <TabsContent value="payments" className="mt-4">
            {payments.length === 0 ? (
              <EmptyState icon={CreditCard} title="No payments yet" description="Collected payments will appear here with gateway references." testid="payments-empty" />
            ) : (
              <Card className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Txn Reference</TableHead><TableHead>Invoice</TableHead><TableHead>Amount</TableHead><TableHead>Gateway</TableHead><TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {payments.map((p) => (
                      <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                        <TableCell className="font-mono text-xs">{p.txn_ref}</TableCell>
                        <TableCell>{p.invoice_number}</TableCell>
                        <TableCell className="font-medium">{money(p.amount)}</TableCell>
                        <TableCell className="capitalize">{p.gateway}</TableCell>
                        <TableCell><StatusBadge value={p.status === "success" ? "paid" : p.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
          <TabsContent value="receipts" className="mt-4">
            {receipts.length === 0 ? (
              <EmptyState icon={ReceiptText} title="No receipts yet" description="Receipts are generated automatically after verified payments." testid="receipts-empty" />
            ) : (
              <Card className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Receipt #</TableHead><TableHead>Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Amount</TableHead><TableHead>Gateway</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {receipts.map((r) => (
                      <TableRow key={r.id} data-testid={`receipt-row-${r.id}`}>
                        <TableCell className="font-medium">{r.receipt_number}</TableCell>
                        <TableCell>{r.invoice_number}</TableCell>
                        <TableCell>{r.customer_name}</TableCell>
                        <TableCell className="font-medium">{money(r.amount)}</TableCell>
                        <TableCell className="capitalize">{r.gateway}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
